import { Component, Injector, Input, OnInit } from '@angular/core';
import { ApiResponse, BaseModalComponent } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { InventoryType } from '../../../../models/inventory/inventory-type';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { InventoryTypeProxyService } from '../../../../proxies/inventory/inventory-type.proxy.service';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Translation } from '../../../../models/translation';
import { LocalHelperService } from '../../../../services/local-helper.service';
import { UserGroup } from '../../../../models/authorization/enum/user-group.enum';
import { ToggleSwitchChangeEvent } from 'primeng/toggleswitch';

@Component({
  selector: 'app-inventory-type-modal',
  templateUrl: './inventory-type-modal.component.html',
  styleUrl: './inventory-type-modal.component.scss',
})
export class InventoryTypeModalComponent extends BaseModalComponent<InventoryType | null> implements OnInit {
  @Input() inventoryType: InventoryType | null = null;

  isLoading = false;
  submittedForm = false;
  showTranslateInputs = false;
  // inventoryType Form
  itemNameFormGroup: FormGroup = new FormGroup({
    en: new FormControl<string | null>(null, [Validators.required]),
    es: new FormControl<string | null>(null),
  });

  itemInventoryFormGroup: FormGroup = new FormGroup({
    name: this.itemNameFormGroup,
    description: new FormControl(''),
  });
  translations: Record<string, Translation> = {
    Add_Inventory_Type: {
      en: "Inventory Type '{{name}}' Saved",
      es: "Tipo de inventario '{{name}}' guardado",
    },
    Update_Inventory_Type: {
      en: "Inventory Type '{{name}}' Updated",
      es: "Tipo de inventario '{{name}}' actualizado",
    },
  };

  constructor(
    injector: Injector,
    private readonly messageService: MessageService,
    private inventoryTypeProxyService: InventoryTypeProxyService,
    private translateService: TranslateService,
    public localHelperTranslate: LocalHelperService,
  ) {
    super(injector);
  }

  canEdit() {
    // if Admin or Editor
    if (AuthService.hasRole(UserGroup.InventoryTypesAdmin)) {
      return true;
    }
    // if Viewer
    if (AuthService.hasRole(UserGroup.InventoryTypesViewer)) {
      return false;
    }
    // Default
    return false;
  }

  ngOnInit(): void {
    if (this.inventoryType?.id) {
      this.patchItemInventoryFormGroup();
      if (!this.canEdit()) {
        this.itemInventoryFormGroup.disable();
        this.itemNameFormGroup.disable();
      }
    }
  }

  patchItemInventoryFormGroup() {
    if (this.inventoryType) {
      // Crear una copia del objeto name para no modificar el original
      const nameValue = {
        en: this.inventoryType.name?.en || '',
        es: this.inventoryType.name?.es || '',
        missingTranslations: this.inventoryType.name?.missingTranslations || [],
      };

      this.clearMissingTranslations(nameValue);

      this.itemInventoryFormGroup.patchValue({
        name: nameValue,
        description: this.inventoryType.description ?? '',
      });

      if (nameValue.missingTranslations?.includes('es')) {
        this.showTranslateInputs = true;
      }
    } else {
      this.itemInventoryFormGroup.patchValue({
        name: { en: '', es: '', missingTranslations: [] },
        description: '',
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private clearMissingTranslations(translationData: any): void {
    if (!translationData || !translationData.missingTranslations) return;

    const langs: ('es' | 'en')[] = ['es', 'en'];

    langs.forEach((lang) => {
      if (translationData.missingTranslations.includes(lang)) {
        translationData[lang] = '';
      }
    });
  }

  onSave() {
    let save$: Observable<ApiResponse<InventoryType>>;
    this.submittedForm = true;

    if (this.itemInventoryFormGroup.valid) {
      this.isLoading = true;
      const inventoryType: InventoryType = {
        id: this.inventoryType?.id ? this.inventoryType.id : null,
        ...this.itemInventoryFormGroup.value,
      };

      if (!this.inventoryType?.id) {
        save$ = this.inventoryTypeProxyService.createInventoryType(inventoryType);
      } else {
        save$ = this.inventoryTypeProxyService.updateInventoryType(this.inventoryType?.id, inventoryType);
      }

      console.log(inventoryType);

      save$.subscribe({
        next: (response) => {
          this.isLoading = false;
          this.close({
            action: 'save',
            data: this.inventoryType?.id ? inventoryType : response.data,
          });
          this.messageService.add({
            severity: 'success',
            summary: this.inventoryType?.id
              ? (this.translations['Update_Inventory_Type'][this.translateService.currentLang as keyof Translation] || '').replace(
                  '{{name}}',
                  this.localHelperTranslate.getLocalizedValue(response.data?.name) ?? '',
                )
              : (this.translations['Add_Inventory_Type'][this.translateService.currentLang as keyof Translation] || '').replace(
                  '{{name}}',
                  this.localHelperTranslate.getLocalizedValue(response.data?.name) ?? '',
                ),
            key: 'br',
            life: 2500,
          });
        },
        error: (error) => {
          this.isLoading = false;
          console.error('❌ Error saving Inventory Type:', error);
        },
      });
    }
  }

  onClose() {
    this.close({ action: 'close', data: null });
  }

  onTranslateChange(event: ToggleSwitchChangeEvent) {
    const isEsRequired = event.checked;

    const esControl = this.itemNameFormGroup.get('es');

    if (isEsRequired) {
      esControl?.addValidators([Validators.required]);
    } else {
      esControl?.clearValidators();
    }

    esControl?.updateValueAndValidity();
  }
}
